import dayjs, { Dayjs } from 'dayjs';

export enum DateFormat {
  dateFormatWithoutYear = 'DD MMM',
  datetimeFormatShort = 'DD MMM HH:mm:ss',
  dateFormat = 'DD MMM YYYY',
  dateFormatAlternative = 'DD.MM.YYYY',
  dateFormatOnlyMonth = 'MMM',
  dateFormatOnlyMonthYear = 'MMM YY',
  dateFormatShortYear = 'YY',
  dateFormatFullMonthYear = 'MMM YYYY',
  dateFormatIso = 'YYYY-MM-DD',
  dateFormatSlashedIso = 'YYYY/MM/DD',
  datetimeFormatStartOfDayDropTz = `${DateFormat.dateFormatIso}T00:00:00`,
  datetimeFormatEndOfDayDropTz = `${DateFormat.dateFormatIso}T23:59:59`,
  datetimeFormatIso = 'YYYY-MM-DD HH:mm:ss',
  datetimeFormatIso8601 = 'YYYY-MM-DDTHH:mm:ss[Z]',
}

const DATE_FORMAT_WITHOUT_YEAR = 'DD MMM';
const DATE_FORMAT = DATE_FORMAT_WITHOUT_YEAR + ' YYYY';
const DATE_FORMAT_ALTERNATIVE = 'DD.MM.YYYY';
const DATE_FORMAT_ONLY_MONTH_YEAR = 'MMM YY';
const DATE_FORMAT_ISO = 'YYYY-MM-DD';
const DATE_FORMAT_SLASHED_ISO = 'YYYY/MM/DD';

const TIME_FORMAT_START_OF_DAY = '00:00:00';
const TIME_FORMAT_END_OF_DAY = '23:59:59';

const DATETIME_FORMAT_START_OF_DAY_DROP_TZ = `${DATE_FORMAT_ISO}T${TIME_FORMAT_START_OF_DAY}`;
const DATETIME_FORMAT_END_OF_DAY_DROP_TZ = `${DATE_FORMAT_ISO}T${TIME_FORMAT_END_OF_DAY}`;

/**
 * @deprecated Use toFormattedDateOrNull instead, which provides a fallback option for invalid dates.
 */
export const formatDate = (date: Date | Dayjs) => dayjs(date).format(DATE_FORMAT);
/**
 * @deprecated Use toFormattedDateOrNull instead, which provides a fallback option for invalid dates.
 */
export const formatDateIso = (date: Date | Dayjs) => dayjs(date).format(DATE_FORMAT_ISO);
/**
 * @deprecated Use toFormattedDateOrNull instead, which provides a fallback option for invalid dates.
 */
export const formatDateSlashIso = (date: Date | Dayjs) => dayjs(date).format(DATE_FORMAT_SLASHED_ISO);
/**
 * @deprecated Use toFormattedDateOrNull instead, which provides a fallback option for invalid dates.
 */
export const formatDateWithoutYear = (date: Date | Dayjs) => dayjs(date).format(DATE_FORMAT_WITHOUT_YEAR);
/**
 * @deprecated Use toFormattedDateOrNull instead, which provides a fallback option for invalid dates.
 */
export const formatDateAlternative = (date: Date | Dayjs) => dayjs(date).format(DATE_FORMAT_ALTERNATIVE);
/**
 * @deprecated Use toFormattedDateOrNull instead, which provides a fallback option for invalid dates.
 */
export const formatDateMonthYear = (date: Date | Dayjs) => dayjs(date).format(DATE_FORMAT_ONLY_MONTH_YEAR);
/**
 * @deprecated Use toFormattedDateOrNull instead, which provides a fallback option for invalid dates.
 */
export const formatDateShortWithYear = (date: Date | Dayjs) => dayjs(date).format(DATE_FORMAT);
/**
 * @deprecated Use toFormattedDateOrNull instead, which provides a fallback option for invalid dates.
 */
export const formatDateTimeStartOfDayDropTZ = (date: Date | Dayjs) =>
  dayjs(date).format(DATETIME_FORMAT_START_OF_DAY_DROP_TZ);
/**
 * @deprecated Use toFormattedDateOrNull instead, which provides a fallback option for invalid dates.
 */
export const formatDateTimeEndOfDayDropTZ = (date: Date | Dayjs) =>
  dayjs(date).format(DATETIME_FORMAT_END_OF_DAY_DROP_TZ);

/**
 * Formats a date using the specified format string. If the date is invalid, it returns null or a provided fallback value.
 * This function is not meant to convert timestamps to date. It should be used for formatting date strings or Date/Dayjs objects.
 * @param date The date to format, which can be a string, Date object, or Dayjs object.
 * @param format The desired date format, specified as a string or a value from the DateFormat enum.
 * @param fallback An optional fallback string to return if the date is invalid. If not provided, it defaults to null.
 * @returns The formatted date string, or the fallback value if the date is invalid.
 */
export function toFormattedDateOrNull(date: number | string | Date | Dayjs, format: DateFormat): string | null;
export function toFormattedDateOrNull(
  date: number | string | Date | Dayjs,
  format: DateFormat,
  fallback: string,
): string;

export function toFormattedDateOrNull(date: number | string | Date | Dayjs, format: DateFormat, fallback?: string) {
  const _date = dayjs(date);

  if (!_date.isValid() || typeof date === 'number') {
    return fallback ?? null;
  }

  return dayjs(date).format(format);
}

export const formatMinutes = (value: number) => {
  const seconds = value % 60;
  const minutes = Math.floor(value / 60);
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds.toFixed(0)}`;
};
export const formatHoursMinutes = (value: number): string => {
  if (value == null || isNaN(value)) {
    return '00:00';
  }
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (value % 60).toFixed(0).padStart(2, '0');

  return hours > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
};
export const replaceMonth = (dateString: string): string =>
  dateString
    ? dateString
        .replace('Jan', 'January')
        .replace('Feb', 'February')
        .replace('Mar', 'March')
        .replace('Apr', 'April')
        .replace('Jun', 'June')
        .replace('Jul', 'July')
        .replace('Aug', 'August')
        .replace('Sep', 'September')
        .replace('Oct', 'October')
        .replace('Nov', 'November')
        .replace('Dec', 'December')
    : '';

/**
 * Formats a given date range into a human-readable string using short month names.
 *
 * This utility function is designed to handle date ranges by formatting the start and end dates
 * according to the following rules:
 * 1. If the start and end dates are within the same year, the year is omitted from the start date for brevity.
 * 2. The start date is formatted with the pattern "DD MMM" (e.g., "01 Jan").
 * 3. The end date is always formatted with the pattern "DD MMM YYYY" (e.g., "31 Dec 2023").
 *
 * The output is a string in the format:
 * "<formattedStartDate> - <formattedEndDate>"
 *
 * Example:
 * Input: [["2023-01-01", "2023-12-31"]]
 * Output: "01 Jan - 31 Dec 2023"
 *
 * @param dateRange - An array containing a single date range pair. Each pair is an array with two strings: start and end dates.
 * @returns A formatted string representing the date range.
 */
export const formatDateRangeWithShortMonth = (startDate: string | Date, endDate: string | Date): string => {
  // Utility to validate and convert a date
  const ensureValidDate = (date: string | Date): Date => {
    if (date === null || date === undefined) {
      throw new Error(`Invalid date: ${date}`);
    }
    const parsedDate = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(parsedDate.getTime())) {
      throw new Error(`Invalid date: ${date}`);
    }
    return parsedDate;
  };

  const start = ensureValidDate(startDate);
  const end = ensureValidDate(endDate);

  const dayMonthOptions: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short' };
  const fullDateOptions: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
  const sameYear = start.getFullYear() === end.getFullYear();

  const formattedStartDate = start.toLocaleDateString('en-GB', sameYear ? dayMonthOptions : fullDateOptions);
  const formattedEndDate = end.toLocaleDateString('en-GB', fullDateOptions);

  return `${formattedStartDate} - ${formattedEndDate}`;
};
